<template>
  <div id="app" class="d-flex flex-fill">
    <component :is="layout" :ready="isInitalized">
      <router-view v-if="isInitalized" />

      <div v-else class="text-center m-auto">
        <b-spinner variant="primary" style="width: 5rem; height: 5rem" />
      </div>
    </component>

    <auth-session-expire-modal />
  </div>
</template>

<script>
import { channelMixin } from './mixins/channelMixin';
import { userInfoMixin } from './mixins/userInfoMixin';
import { zendeskMixin } from './mixins/zendeskMixin';
import { optimizelyMixin } from './mixins/optimizelyMixin';

import LearningUnitType from './models/LearningUnitType';
// import SectionRepeat from "./models/SectionRepeat";
import Status from './models/Status';
import UserRole from './models/UserRole';
import { CatalogConsent } from './models/catalog/CatalogConsent';
import { Auth } from './models/Auth';
import head from './configs/head-config';
import AuthSessionExpireModal from './components/Auth/SessionExpireModal.vue';


const default_layout = 'default';

export default {
  name: 'App',

  components: {
    AuthSessionExpireModal
  },

  mixins: [channelMixin, userInfoMixin, zendeskMixin, optimizelyMixin],

  data() {
    return {
      cancelDefaultLayout: [
        'SignIn',
        'SignUp',
        'reset-password',
        'forgot-password',
        'PDPA',
        'verify-email',
        'send-verify-email',
        'profile/complete',
        'on-boarding',
        'AuthCallBack'
      ],
      ready: false,
      checkPdpaAndVerify: false,
      firebaseUser: null
    };
  },

  computed: {
    layout() {
      if (this.cancelDefaultLayout.includes(this.page)) {
        return 'no-layout';
      } else {
        return (this.$route.meta.layout || default_layout) + '-layout';
      }
    },

    page() {
      return this.$route.name;
    },

    isInitalized() {
      return Auth.isInitialized() && this.ready;
    }
  },

  watch: {
    $route: 'onRouteChanged'
  },

  methods: {
    async onRouteChanged(route) {
      console.log('[App.vue] on route changed: ', route);

      if (!this.ready) {
        await Promise.all([
          LearningUnitType.api().fetch(),
          Status.api().fetch(),
          UserRole.api().fetch()
        ]);


      }

      if (this.user) {
        if (route.name && route.name !== 'verify-email') {
          if (
            (route.name == 'learning-unit' || route.name == 'review-learning-unit') &&
            route.name !== 'PDPA'
          ) {
            const isUserConsentInvalid = await this.validateUserConsent();

            if (isUserConsentInvalid) {
              return this.$router.push({ name: 'PDPA' });
            }
          }

          if (route.name !== 'PDPA' && route.name !== 'send-verify-email') {
            const isUserConfirmInvalid = this.validateUserConfirmationStatus();

            if (isUserConfirmInvalid) {
              return this.$router.push({ path: '/send-verify-email' });
            }
          }

          if (this.user.isGoogleUser() && route.name !== 'on-boarding') {
            const isUserOnboardingInvalid = await this.validateUserOnBoardingStatus();

            if (isUserOnboardingInvalid) {
              return this.$router.push({ path: '/on-boarding' });
            }
          }
        }
      }

      this.ready = true;
    },

    async validateUserConsent() {
      let invalid = false;

      if (this.user) {
        const hasConsented = await this.getConsentDocuments();
        // invalid is true if they have NOT consented
        invalid = !hasConsented;
      }

      return invalid;
    },

    validateUserConfirmationStatus() {
      let invalid = false;

      if (this.user) {
        console.log('[App.vue] checked user confirmed: ', this.user.confirmed);

        invalid = !this.user.confirmed;
      }

      return invalid;
    },

    async validateUserOnBoardingStatus() {
      let invalid = false;
      if (!this.$_isFeatureEnabled('onboarding')) {
        return invalid;
      }

      if (this.user) {
        console.log('[App.vue] checked user onboarding: ', this.user.is_onboarded);

        invalid = !this.user.is_onboarded;
      }

      return invalid;
    },

    async getConsentDocuments() {
      try {
        const response = await CatalogConsent.api().requestConsents();
        const data = response?.response?.data?.data;
        
        const hasConsented = data?.consents ? data.consents.length === 0 : true;
        
        console.log('[App.vue] checked user consent status: ', hasConsented);

        return hasConsented;
      } catch (error) {
        console.error('[App.vue] Failed to check user consent: ', error);
        return false; // Force re-consent on error
      }
    }
  },

  head
};
</script>

<style lang="scss">
@import './assets/ui-fort/bamburgh.scss';
</style>
